import classNames from 'classnames'
import moment from 'moment-timezone'
import { confirmDialog } from 'primereact/confirmdialog'
import { React } from 'react'
import {
  DEFAULT_DATETIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  NOT_AVAILABLE,
  GMAP_OPTIONS,
  SegmentsThresholds
} from '../static/config'
import {
  gmapPolyline,
  gmapPolylineHigh,
  gmapPolylineLow,
  gmapPolylineMidHigh,
  gmapPolylineMidLow
} from './GoogleMaps'
import { Messages } from '../static/messages'
import api from './api'

export const prettyFloat = (value, precision = 2) => {
  const number = parseFloat(value || 0).toFixed(precision)
  return number.toString().replace('.', ',')
}

export const objectToQueryString = (obj) => {
  const params = []
  const keys = Object.keys(obj)
  keys.forEach((key, index) => {
    params.push(`${key}=${obj[key]}`)
  })
  return '?' + params.join('&')
}

export const secondsToDhms = (seconds) => {
  seconds = Number(seconds)
  const secondsInDay = 86400
  return {
    d: Math.floor(seconds / secondsInDay),
    h: Math.floor((seconds % secondsInDay) / 3600),
    m: Math.floor((seconds % 3600) / 60),
    s: Math.floor(seconds % 60),
    secondsInDay
  }
}

export const secondsToTime = (seconds, fmt) => {
  const dhms = secondsToDhms(seconds)
  const days = dhms.d > 0 ? `${dhms.d} gg ` : ''
  seconds = seconds - 86400 * dhms.d
  const prettyTime = moment.utc(seconds * 1000).format(fmt || 'HH:mm:ss')
  return `${days}${prettyTime}`
}

export const formatDatetime = (date, fmt) => {
  const _format = fmt || DEFAULT_DATETIME_FORMAT
  return moment.utc(date).local().format(_format)
}

export const formatDate = (date, fmt) => {
  return formatDatetime(date, fmt || DEFAULT_DATE_FORMAT)
}

export const formatTime = (date, fmt) => {
  return formatDatetime(date, fmt || DEFAULT_TIME_FORMAT)
}

export const stringToDate = (strDate) => {
  return moment.utc(strDate).local().toDate()
}

export const ISODateString = (date) => {
  return moment.utc(date).local().toISOString()
}

export const ISODateRangeDay = (dateStart, dateEnd) => {
  const tzDateStart = moment.utc(dateStart).local().startOf('day')
  const tzDateEnd = moment.utc(dateEnd).local().endOf('day')
  return [tzDateStart.toISOString(), tzDateEnd.toISOString()]
}

export const ISODateRangeSingleDay = (date) => {
  return ISODateRangeDay(date, date)
}

export const diffRangeDays = (start, end) => {
  return moment.utc(end).diff(moment.utc(start), 'days')
}

export const dateAddDay = (date, days) => {
  return moment(date).add(days, 'days').toDate()
}

export const deleteConfirmDialog = (acceptHandler, rejectHandler, message) => {
  confirmDialog({
    message: message || 'Sei sicuro di voler continuare?',
    header: 'Avviso',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Elimina',
    acceptClassName: 'p-button-danger',
    rejectLabel: 'Indietro',
    accept: acceptHandler,
    reject: rejectHandler
  })
}

export const HttpErrorDecode = (error) => {
  console.debug('received error from api: ' + JSON.stringify(error.response.data))
  const status = error.response.data.status || 0
  const errorResp = error.response.data.response

  if (status === 404) {
    return Messages.HttpErrorDecode.notFound
  }
  if (status === 409) {
    if (errorResp && errorResp.cause && errorResp.cause.columns) {
      return `${Messages.HttpErrorDecode.conflict} ${errorResp.cause.columns.join(', ')}`
    }
    return Messages.HttpErrorDecode.conflictData
  }
  if (status === 429) {
    return Messages.HttpErrorDecode.manyRequest
  }
  if (status === 502) {
    return Messages.HttpErrorDecode.badGateway
  }
  return Messages.HttpErrorDecode.unexpectedError
}

export const showToastError = (toast, error) => {
  const decodeError = (error) => {
    if (typeof error === 'string') return error
    if (typeof error.response === 'string') return error.response
    if (error.response) return HttpErrorDecode(error)
    return 'Si è verificato un errore imprevisto!'
  }

  toast.current.show({
    severity: 'error',
    summary: Messages.ToastError.summary,
    detail: decodeError(error)
  })
}

export const showToastSuccess = (toast, message) => {
  toast.current.show({
    severity: 'success',
    summary: Messages.ToastSuccess.summary,
    detail: message || Messages.ToastSuccess.detail
  })
}

export const getAddress = async (lat, lon) => {
  const message = Messages.GetAddress.streetNotFound
  try {
    const response = await api.geoNearbyStreet(lat, lon)
    if (!httpcodeIsOK(response.status)) {
      return message
    }
    const data = await response.data
    return data.address
  } catch (error) {
    console.error(error)
    return message
  }
}

export const MarkerInfoContentTemplate = (info) => {
  const markerFooter = 'Apri Google Maps'
  const gmapUrl = `https://www.google.it/maps/@${info.position.lat()},${info.position.lng()}`

  return `
    <div class='infoWindowContent'>
      <div class="infoWindowContent__vehicle p-mb-2">
        <div class="infoWindowContent__vehicle title">${info.title}</div>
        <div class="infoWindowContent__vehicle body">
          ${info.data || NOT_AVAILABLE}
        </div>
      </div>
      <div class="infoWindowContent__dateTime p-my-1">
        <div class="infoWindowContent__dateTime title">Data e ora</div>
        <div class="infoWindowContent__dateTime body">
          <p class="p-d-flex p-ai-center p-my-1">
            <i class="pi pi-calendar text-muted"></i> &nbsp;
            ${formatDatetime(info.dateTime)}
          </p>
        </div>
        <p class="p-mt-3">
        <a target="_blank" jstcache="6" href="${gmapUrl}" tabindex="0">
          <span>${markerFooter}</span>
        </a>
        </p>
      </div>
    </div>
  `
}

export const PolylineInfoContentTemplate = (info) => {
  return `
    <div class='infoWindowContent'>
      <div class="infoWindowContent__vehicle p-mb-2">
        <div class="infoWindowContent__vehicle title">Info Segmento</div>
        <div class="infoWindowContent__vehicle body">
          <table>
            <tr>
              <td style="padding:3px 5px 0 0"><i class="pi pi-clock p-mr-2"></i>&nbsp;Durata</td>
              <td style="padding:3px 5px 0 0">&nbsp;<strong>${secondsToTime(info.duration)}</strong>
            </tr>
            <tr>
              <td style="padding:3px 5px 0 0"><i class="pi pi-map-marker p-mr-2"></i>&nbsp;Km percorsi</td>
              <td style="padding:3px 5px 0 0">&nbsp;<strong>${prettyFloat(info.meters / 1000, 3)} Km</strong>
            </tr>
            <tr>
              <td style="padding:3px 5px 0 0"><i class="pi pi-chart-line p-mr-2"></i>&nbsp;Velocità massima</td>
              <td style="padding:3px 5px 0 0">&nbsp;<strong>${info.maxSpeed} Km/h</strong>
            </tr>
            <tr>
              <td style="padding:3px 5px 0 0"><i class="pi pi-chart-line p-mr-2"></i>&nbsp;Velocità media</td>
              <td style="padding:3px 5px 0 0">&nbsp;<strong>${info.avgSpeed} Km/h</strong>
            </tr>
          </table>
        </div>
      </div>
    </div>
  `
}

export const paginationFromHeaders = (headers) => {
  return {
    currentPage: Number(headers['x-pagination-page']),
    totalRecords: Number(headers['x-pagination-count']),
    totalPages: Number(headers['x-pagination-num-pages']),
    pageSize: Number(headers['x-pagination-page-size'])
  }
}

export const httpcodeIsOK = (code) => {
  return Math.floor(code / 100) === 2
}

export const paginatorTemplate = {
  layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink',
  PrevPageLink: (options) => {
    return (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <span className="p-p-3">
          <i className="pi pi-angle-left"></i>
        </span>
      </button>
    )
  },
  NextPageLink: (options) => {
    return (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <span className="p-p-3">
          <i className="pi pi-angle-right"></i>
        </span>
      </button>
    )
  },
  PageLinks: (options) => {
    if (
      (options.view.startPage === options.page &&
        options.view.startPage !== 0) ||
      (options.view.endPage === options.page &&
        options.page + 1 !== options.totalPages)
    ) {
      const className = classNames(options.className, { 'p-disabled': true })

      return (
        <span className={className} style={{ userSelect: 'none' }}>
          ...
        </span>
      )
    }

    return (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
      >
        {options.page + 1}
      </button>
    )
  }
}

export const GoToRoute = (history, route) => {
  setTimeout(
    () => { history.push(route) },
    process.env.REACT_APP_GOTO_ROUTE_DELAY || 50
  )
}

export const isInRange = (range, value) => {
  const checkLeft = (r, v) => {
    if (r.close && r.value <= v) return true
    if (!r.close && r.value < v) return true
    return false
  }

  const checkRight = (r, v) => {
    if (r.close && r.value >= v) return true
    if (!r.close && r.value > v) return true
    return false
  }

  return checkLeft(range.min, value) && checkRight(range.max, value)
}

export const sortTable = (table, event) => {
  const getItem = (data, key) => {
    let tmp = data
    const keys = key.split('.')
    for (const k of keys) {
      if (tmp) tmp = tmp[k]
      else return ''
    }
    return tmp
  }

  if (event.sortOrder === 1) {
    table.sort((a, b) => getItem(a, event.sortField) >= getItem(b, event.sortField) ? 1 : -1)
  } else {
    table.sort((a, b) => getItem(a, event.sortField) < getItem(b, event.sortField) ? 1 : -1)
  }
}

export const buildLine = (coordinates, polyline = gmapPolyline) => {
  return polyline(coordinates.map((item) => { return { lat: item.lat, lng: item.lon } }))
}

export const coordinatesSplitter = (gmap, positions) => {
  const Segments = {
    LOW: { coordinates: [], ...SegmentsThresholds.LOW, type: gmapPolylineLow },
    MID_LOW: { coordinates: [], ...SegmentsThresholds.MID_LOW, type: gmapPolylineMidLow },
    MID: { coordinates: [], ...SegmentsThresholds.MID, type: gmapPolyline },
    MID_HIGH: { coordinates: [], ...SegmentsThresholds.MID_HIGH, type: gmapPolylineMidHigh },
    HIGH: { coordinates: [], ...SegmentsThresholds.HIGH, type: gmapPolylineHigh }
  }
  const segmentTypes = [
    Segments.LOW,
    Segments.MID_LOW,
    Segments.MID,
    Segments.MID_HIGH,
    Segments.HIGH
  ]

  const registerEvents = (element, segment) => {
    const coordinates = segment.coordinates
    const style = GMAP_OPTIONS.polylineStyle
    element.addListener('mouseover', function () {
      element.setOptions({ strokeWeight: style.strokeWeight + 3, strokeOpacity: 1 })
    })
    element.addListener('mouseout', function () {
      element.setOptions({ strokeWeight: style.strokeWeight, strokeOpacity: style.strokeOpacity })
    })
    element.addListener('click', function () {
      const infoWindow = new window.google.maps.InfoWindow()
      let avgSpeed = coordinates.reduce((a, b) => a + b.speed, 0) / coordinates.length
      if (avgSpeed < segment.minValue) avgSpeed = segment.minValue + 1
      if (avgSpeed > segment.threshold) avgSpeed = segment.threshold - 1
      infoWindow.setContent(
        PolylineInfoContentTemplate({
          meters: element.meters(),
          duration: Math.abs(coordinates[0].ts - coordinates.at(-1).ts),
          maxSpeed: Math.max(...coordinates.map((c) => c.speed)),
          avgSpeed: parseInt(Math.round(avgSpeed))
        })
      )
      infoWindow.open(gmap, element)
    })
  }

  const coordinates = []
  let switcher = Segments.LOW

  const splitter = (nextSegment) => {
    if (switcher.coordinates.length < GMAP_OPTIONS.minSegmentLen) return switcher
    nextSegment.coordinates.push(switcher.coordinates[switcher.coordinates.length - 1])
    const line = buildLine(switcher.coordinates, switcher.type)
    registerEvents(line, switcher)
    coordinates.push(line)
    switcher.coordinates = []
    return nextSegment
  }

  for (const point of positions) {
    for (const segment of segmentTypes) {
      if (Math.round(point.speed) < segment.threshold) {
        if (switcher !== segment) {
          switcher = splitter(segment)
        }
        break
      }
    }
    switcher.coordinates.push(point)
  }

  for (const segmentKey in Segments) {
    const segment = Segments[segmentKey]
    if (segment.coordinates) {
      const line = buildLine(segment.coordinates, segment.type)
      registerEvents(line, segment)
      coordinates.push(line)
      break
    }
  }

  return coordinates
}
